"use client";

import Image from "next/image";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { PublicLink as Link } from "@/components/public/PublicLink";
import { LanguageSwitcherButton } from "@/components/ui/language-switcher";
import { ThemeSwitcher } from "@/components/ui/theme-switcher";
import { useUser } from "@/hooks/useUser";
import NextLink from "next/link";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useJsonTranslation("Header");
  const { user, loading } = useUser();

  const navLinks = [
    { labelKey: "home.nav.features", href: "/features" },
    { labelKey: "home.nav.pricing", href: "/seeds" },
    { labelKey: "home.nav.howItWorks", href: "/how-to" },
    { labelKey: "home.nav.download", href: "/download" },
    { labelKey: "home.nav.blog", href: "/blog" },
    { labelKey: "home.nav.movieArchitect", href: "https://moviearchitect.ai" },
  ];

  return (
    <header className="border-border/30 fixed top-0 right-0 left-0 z-50 border-b pt-[env(safe-area-inset-top)]">
      <div className="glass">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2">
              <Image
                src="/logo-icon.png"
                alt="VidSeeds.ai"
                width={28}
                height={28}
                className="h-7 w-auto"
                priority
              />
              <span className="font-display text-foreground text-xl font-bold tracking-tight">
                VidSeeds
                <span className="text-primary">.ai</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden items-center gap-8 md:flex">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                >
                  {t(link.labelKey)}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden items-center gap-4 md:flex">
              <ThemeSwitcher />
              <LanguageSwitcherButton />
              {!loading &&
                (user ? (
                  <NextLink href="/dashboard">
                    <Button size="sm">{t("home.header.dashboard")}</Button>
                  </NextLink>
                ) : (
                  <>
                    <Link href="/login">
                      <Button variant="ghost" size="sm">
                        {t("home.header.login")}
                      </Button>
                    </Link>
                    <Link href="/signup">
                      <Button size="sm">{t("home.header.startTrial")}</Button>
                    </Link>
                  </>
                ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="glass glass-hover text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded-xl p-2 focus-visible:ring-2 focus-visible:outline-none md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={t("home.header.menuToggle")}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="border-border/50 border-t py-4 md:hidden">
              <nav className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t(link.labelKey)}
                  </Link>
                ))}
                <div className="border-border/50 flex flex-col gap-2 border-t pt-4">
                  <div className="flex items-center gap-2 px-2">
                    <ThemeSwitcher />
                    <LanguageSwitcherButton />
                  </div>
                  {!loading &&
                    (user ? (
                      <NextLink
                        href="/dashboard"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Button className="w-full">
                          {t("home.header.dashboard")}
                        </Button>
                      </NextLink>
                    ) : (
                      <>
                        <Link
                          href="/login"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Button variant="ghost" className="w-full">
                            {t("home.header.login")}
                          </Button>
                        </Link>
                        <Link
                          href="/signup"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Button className="w-full">
                            {t("home.header.startTrial")}
                          </Button>
                        </Link>
                      </>
                    ))}
                </div>
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
